package com.evoapps.evoshift.widget

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.model.Appearance
import com.evoapps.evoshift.domain.model.BreakRules
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.workerId
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.usecase.roster.GetRosterShiftsUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.session.WorkplaceSessionManager
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.isoDayNumber
import kotlinx.datetime.minus
import kotlinx.datetime.number
import kotlinx.datetime.plus
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Duration
import kotlin.time.Instant

/** Where the app leaves the widget snapshot; platforms without home-screen widgets ignore it. */
fun interface ShiftWidgetStore {
    /** null clears the widget (signed out). */
    fun save(snapshot: ShiftWidgetSnapshot?)
}

/** Where the app leaves the watch's snapshot (SH-99); platforms without a watch app ignore it. */
fun interface WatchSnapshotStore {
    fun save(snapshot: WatchSnapshot)
}

/**
 * Keeps the home-screen widget's snapshot up to date while the app runs: the signed-in user's
 * published shifts for the next [DAYS] days (live, from Firestore) and who works today and
 * tomorrow at the current workplace. Signing out clears it. The paired watch (SH-99) gets the
 * same shifts from the start of the week, with their clock times ([watchSnapshot]).
 *
 * SH-200: the snapshot holds each of the person's active workplaces with its team (each widget
 * shows the workplaces it was set up for, ShiftWidgetContent.select) and the colours of the
 * [appearance] picked in the app; a new pick rewrites it.
 */
class ShiftWidgetSync(
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val shiftRepository: ShiftRepository,
    private val getRosterShiftsUseCase: GetRosterShiftsUseCase,
    private val sessionManager: WorkplaceSessionManager,
    private val store: ShiftWidgetStore,
    private val watchStore: WatchSnapshotStore = WatchSnapshotStore { },
    /** SH-196/SH-200: the appearance picked in the app (ThemeSettings.appearance). */
    private val appearance: StateFlow<Appearance> = MutableStateFlow(Appearance.DEFAULT),
    private val now: () -> Instant = { AppClock.now() },
    private val timeZone: () -> TimeZone = { TimeZone.currentSystemDefault() }
) {
    @OptIn(ExperimentalCoroutinesApi::class)
    fun start(scope: CoroutineScope): Job = scope.launch {
        combine(getCurrentUserUseCase(), sessionManager.currentWorkplaceId, appearance) { user, workplaceId, look -> Triple(user, workplaceId, look) }
            .distinctUntilChanged()
            .collectLatest { (user, workplaceId, look) ->
                if (user == null) {
                    signedOut()
                    return@collectLatest
                }
                val today = now().toLocalDateTime(timeZone()).date
                shiftRepository.observeShiftsForUser(user.uid, since(today).toString()).collect { mine ->
                    store.save(snapshot(user, workplaceId, mine, today, look))
                    watchStore.save(watchSnapshot(user, mine, today))
                }
            }
    }

    /**
     * One refresh without the UI (the widget's background worker, a push, the watch asking):
     * reads the signed-in user's shifts once and saves new snapshots; signed out clears them.
     */
    suspend fun refreshOnce() {
        val user = getCurrentUserUseCase().first()
        if (user == null) {
            signedOut()
            return
        }
        val today = now().toLocalDateTime(timeZone()).date
        val mine = shiftRepository.observeShiftsForUser(user.uid, since(today).toString()).first()
        store.save(snapshot(user, sessionManager.currentWorkplaceId.value, mine, today))
        watchStore.save(watchSnapshot(user, mine, today))
    }

    /**
     * A refresh after a push (SH-96): the push says the server has news, but a fresh listener's
     * first snapshot usually comes from the offline cache. Keeps listening for up to [window] (iOS
     * gives a background push about 30 s) and saves every snapshot, so the server's one lands in
     * the widget and on the watch; the stores skip unchanged ones.
     */
    suspend fun refreshWithin(window: Duration) {
        val user = getCurrentUserUseCase().first()
        if (user == null) {
            signedOut()
            return
        }
        val today = now().toLocalDateTime(timeZone()).date
        withTimeoutOrNull(window) {
            shiftRepository.observeShiftsForUser(user.uid, since(today).toString()).collect { mine ->
                store.save(snapshot(user, sessionManager.currentWorkplaceId.value, mine, today))
                watchStore.save(watchSnapshot(user, mine, today))
            }
        }
    }

    private fun signedOut() {
        store.save(null)
        watchStore.save(WatchSnapshot.signedOut(now().toString()))
    }

    /** The watch shows the whole week, and a shift from yesterday may still be running. */
    private fun since(today: LocalDate): LocalDate = minOf(weekStart(today), today.minus(DatePeriod(days = 1)))

    private fun weekStart(today: LocalDate): LocalDate = today.minus(DatePeriod(days = today.dayOfWeek.isoDayNumber - 1))

    /**
     * The watch's copy (SH-99): the user's published shifts from the start of the week (or
     * yesterday) to [DAYS] days ahead, any workplace, with ids and clock times so the watch can
     * offer clocking in and out.
     */
    fun watchSnapshot(user: User, myShifts: List<Shift>, today: LocalDate): WatchSnapshot {
        val horizon = today.plus(DatePeriod(days = DAYS))
        val shifts = myShifts
            .filter { it.isPublished && it.workerId == user.uid }
            .filter { LocalDate.parse(it.date) in since(today)..horizon }
            .sortedBy { it.plannedTime.start }
            .map { shift ->
                shift.toWidgetShift(user).copy(
                    id = shift.id,
                    clockedIn = shift.actualTime?.start?.takeIf { it.isNotEmpty() },
                    clockedOut = shift.actualTime?.end?.takeIf { it.isNotEmpty() },
                    onBreakSince = BreakRules.open(shift.breaks)?.start
                )
            }
        return WatchSnapshot(
            generatedAt = now().toString(), userName = user.displayName, shifts = shifts,
            // SH-137: the watch speaks the phone app's picked language (a pick saves the profile,
            // whose new copy rebuilds this snapshot).
            language = com.evoapps.evoshift.util.AppLocale.chosen.value
        )
    }

    /**
     * Builds the snapshot from the user's shifts; reads today's and tomorrow's team of each of the
     * user's active workplaces once (SH-200; each widget picks its workplaces). At most
     * [MY_SHIFTS_PER_WORKPLACE] of the user's shifts and [TEAM_PER_WORKPLACE] team shifts per
     * workplace, so the snapshot stays small.
     */
    suspend fun snapshot(
        user: User,
        workplaceId: String?,
        myShifts: List<Shift>,
        today: LocalDate,
        appearance: Appearance = this.appearance.value
    ): ShiftWidgetSnapshot {
        val horizon = today.plus(DatePeriod(days = DAYS))
        val mine = myShifts
            .filter { it.isPublished && it.workerId == user.uid }
            .filter { LocalDate.parse(it.date) in today..horizon }
            .sortedBy { it.plannedTime.start }
            .map { it.toWidgetShift(user) }
            .groupBy { it.workplaceId }
            .flatMap { (_, shifts) -> shifts.take(MY_SHIFTS_PER_WORKPLACE) }
            .sortedBy { it.start }

        val active = user.engagements.entries.filter { it.value.isActive }
        val defaultWorkplace = workplaceId?.takeIf { user.engagements[it]?.isActive == true }
            ?: (active.firstOrNull { it.value.isPrimary } ?: active.firstOrNull())?.key
        // The default workplace first: the order the widget's settings list them in.
        val workplaces = active.sortedBy { if (it.key == defaultWorkplace) 0 else 1 }.map { (id, engagement) ->
            WidgetWorkplace(id = id, name = engagement.cachedWorkplaceName ?: id, team = team(id, today))
        }

        return ShiftWidgetSnapshot(
            generatedAt = now().toString(),
            userName = user.displayName,
            myShifts = mine,
            workplaces = workplaces,
            defaultWorkplaceId = defaultWorkplace,
            colors = WidgetPalette.snapshotColors(appearance)
        )
    }

    /** Who works at [workplaceId] today and tomorrow (published, assigned shifts). */
    private suspend fun team(workplaceId: String, today: LocalDate): List<WidgetShift> {
        val tomorrow = today.plus(DatePeriod(days = 1))
        return listOf(today, tomorrow).map { RosterIds.of(workplaceId, it.year, it.month.number) }.distinct()
            .flatMap { rosterId -> getRosterShiftsUseCase(rosterId).getOrNull().orEmpty() }
            .filter { it.isPublished && it.userId != null && LocalDate.parse(it.date).let { d -> d == today || d == tomorrow } }
            .sortedBy { it.plannedTime.start }
            .take(TEAM_PER_WORKPLACE)
            .map { WidgetShift(it.plannedTime.start, it.plannedTime.end, it.requiredRole, person = it.userDisplayName ?: "?") }
    }

    private fun Shift.toWidgetShift(user: User): WidgetShift {
        val workplaceId = RosterIds.workplaceIdOf(rosterId)
        return WidgetShift(
            start = plannedTime.start,
            end = plannedTime.end,
            role = requiredRole,
            workplaceName = user.engagements[workplaceId]?.cachedWorkplaceName,
            workplaceId = workplaceId
        )
    }

    companion object {
        const val DAYS = 14
        /** SH-200: the user's shifts per workplace in the widget's snapshot. */
        const val MY_SHIFTS_PER_WORKPLACE = 12
        /** SH-200: team shifts (today and tomorrow) per workplace in the widget's snapshot. */
        const val TEAM_PER_WORKPLACE = 24
    }
}
